import { createElement, isValidElement } from "react";
import { createPortal } from "react-dom";
import type { ComponentType, ReactNode } from "react";
import { motion as Motion, AnimatePresence } from "motion/react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

type Variant = "danger" | "warning" | "success";

const VARIANT_STYLES: Record<Variant, { bar: string; iconWrap: string; confirm: string }> = {
  danger: {
    bar: "bg-rose-500",
    iconWrap: "bg-rose-50 text-rose-600",
    confirm: "bg-rose-600 hover:bg-rose-700",
  },
  warning: {
    bar: "bg-amber-500",
    iconWrap: "bg-amber-50 text-amber-600",
    confirm: "bg-amber-600 hover:bg-amber-700",
  },
  success: {
    bar: "bg-emerald-500",
    iconWrap: "bg-emerald-50 text-emerald-600",
    confirm: "bg-emerald-600 hover:bg-emerald-700",
  },
};

export interface ConfirmModalProps {
  open: boolean;
  title: string;
  description: string;
  /** Optional secondary line shown between title and description (e.g. the entity name) */
  subtitle?: string;
  variant?: Variant;
  /** Lucide icon component or any ReactNode */
  icon?: ComponentType<{ size?: number | string; className?: string }> | ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmLoading?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export function ConfirmModal({
  open,
  title,
  description,
  subtitle,
  variant = "danger",
  icon: Icon,
  confirmLabel = "Confirmer",
  cancelLabel = "Annuler",
  confirmLoading = false,
  onClose,
  onConfirm,
}: ConfirmModalProps) {
  const styles = VARIANT_STYLES[variant];

  const iconNode =
    Icon === undefined
      ? null
      : isValidElement(Icon)
        ? Icon
        : createElement(Icon as ComponentType<{ size?: number }>, { size: 30 });

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <Motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-slate-950/45 backdrop-blur-sm"
            onClick={onClose}
          />

          <Motion.div
            key="modal"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="relative w-full max-w-md overflow-hidden rounded-2xl border border-white/20 bg-white shadow-2xl"
          >
            <div className={cn("h-2", styles.bar)} />

            <div className="p-6">
              {iconNode && (
                <div
                  className={cn(
                    "mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl",
                    styles.iconWrap,
                  )}
                >
                  {iconNode}
                </div>
              )}

              <h3 className="mb-2 text-center text-xl font-black tracking-tight text-[#003366] uppercase">
                {title}
              </h3>

              {subtitle && (
                <p className="mb-1 text-center text-sm font-black text-gray-700 uppercase tracking-tight">
                  {subtitle}
                </p>
              )}

              <p className="mx-auto mb-6 max-w-[280px] text-center text-sm font-medium leading-relaxed text-gray-500">
                {description}
              </p>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={confirmLoading}
                  className="flex-1 rounded-xl bg-gray-50 py-3 text-[10px] font-black tracking-[0.2em] text-gray-500 uppercase transition-all hover:bg-gray-100 disabled:opacity-40"
                >
                  {cancelLabel}
                </button>
                <button
                  type="button"
                  onClick={onConfirm}
                  disabled={confirmLoading}
                  className={cn(
                    "flex-1 rounded-xl py-3 text-[10px] font-black tracking-[0.2em] text-white uppercase transition-all flex items-center justify-center gap-1.5",
                    styles.confirm,
                    confirmLoading && "opacity-70",
                  )}
                >
                  {confirmLoading && <Loader2 size={12} className="animate-spin" />}
                  {confirmLabel}
                </button>
              </div>
            </div>
          </Motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
